import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, useTokens, type UserOut } from '../lib/api';

/** Signed-in dashboard home. Operator's first stop after login: the
 * MCP connection details, copy-pasteable setup snippets for each
 * agent client, and a quick map of what they can do.
 *
 * Modeled after Signaliz's onboarding home — same shape, with our
 * actual primitives (Sendepot MCP at /mcp, Bearer ie_… tokens,
 * tenants/mailboxes/sequencer keys as the verbs). */
export default function AppHome() {
  const me = useQuery({ queryKey: ['users-me'], queryFn: api.userMe });
  const u = me.data && 'role' in me.data ? (me.data as UserOut) : null;
  const tokens = useTokens();
  const liveTokens = (tokens.data || []).filter(t => !t.revoked_at);
  const newestToken = liveTokens[0]; // listTokens orders by created_at desc

  return (
    <div className="px-8 py-10 max-w-5xl mx-auto">
      {/* Hero */}
      <header className="text-center mb-12 animate-slide-up">
        <div className="flex justify-center mb-5">
          <span className="w-12 h-12 rounded-full bg-accent/15 border border-accent/40 flex items-center justify-center shadow-[0_0_24px_-4px_rgba(139,92,246,0.4)]">
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-accent" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12h4l2-7 4 14 2-7h6" />
            </svg>
          </span>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 rounded-full border border-line bg-panel text-[11px] text-dim">
          <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
          The engine for AI-driven cold-email infra
        </div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-3">
          <span className="text-ink">Welcome to </span>
          <span className="bg-gradient-to-r from-accent via-run to-emerald-400 bg-clip-text text-transparent italic">Sendepot</span>
          {u?.name ? <>,<br /><span className="text-ink">{u.name.split(' ')[0]}</span></> : null}
        </h1>
        <p className="text-sm md:text-base text-dim max-w-xl mx-auto leading-relaxed">
          Provision Microsoft 365 inboxes from the web, the API, or directly from
          Claude / Cursor / any MCP client. Connect your agent in under a minute.
        </p>
      </header>

      {/* MCP connection card */}
      <ConnectCard hasToken={liveTokens.length > 0} tokenLabel={newestToken?.name} />

      {/* Setup guides */}
      <SetupGuides />

      {/* What you can do */}
      <section className="mb-12">
        <SectionHead
          icon={<IconSparkle />}
          title="What you can do"
          sub="The same surface from the UI, the REST API, and through Claude."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          <Capability
            tone="accent" icon={<IconBolt />}
            title="Provision a tenant"
            body="Hand off a Microsoft 365 tenant + sending domain. Pick how many mailboxes you want (25–150) and we OAuth them all into your sequencer."
          />
          <Capability
            tone="run" icon={<IconShield />}
            title="Watch progress live"
            body="Real-time progress per tenant — connected mailbox count climbs as provisioning runs."
          />
          <Capability
            tone="emerald" icon={<IconRefresh />}
            title="Reconnect dropped mailboxes"
            body="OAuth tokens age out. One click (or one MCP call) reissues the handshake."
          />
          <Capability
            tone="warn" icon={<IconSwap />}
            title="Domain swap"
            body="Burned a domain? Move every mailbox on a tenant to a fresh domain in one operation."
          />
          <Capability
            tone="purple" icon={<IconKey />}
            title="Multiple sequencer keys"
            body="One workspace can hold many Instantly + Smartlead accounts. Pin tenants to specific keys."
          />
          <Capability
            tone="cyan" icon={<IconExport />}
            title="Bulk credentials export"
            body="Multi-select tenants, download a single CSV with full_name, email, password for any sequencer."
          />
        </div>
      </section>

      {/* Resources */}
      <section className="mb-8">
        <SectionHead
          icon={<IconBook />}
          title="Resources"
          sub="Everything you need to ship from."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <ResourceCard
            to="/tenants"
            icon={<IconGrid />}
            title="Tenants"
            sub="Your provisioning canvas"
          />
          <ResourceCard
            to="/tokens"
            icon={<IconKey />}
            title="API tokens"
            sub="Mint and revoke"
          />
          <ResourceCard
            to="/docs"
            icon={<IconBook />}
            title="Docs"
            sub="Guides + best practices"
          />
          <ResourceCard
            href="/api/docs" external
            icon={<IconCode />}
            title="API & MCP reference"
            sub="OpenAPI + MCP tools"
          />
        </div>
      </section>
    </div>
  );
}


// ── Connection card ──────────────────────────────────────────────────────

function ConnectCard({ hasToken, tokenLabel }: { hasToken: boolean; tokenLabel?: string }) {
  const [pasted, setPasted] = useState('');

  const previewKey =
    pasted.startsWith('ie_') && pasted.length > 12
      ? `${pasted.slice(0, 8)}…${pasted.slice(-4)}`
      : 'ie_YOUR_TOKEN_HERE';

  return (
    <section className="card p-6 mb-12 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 right-1/3 w-[400px] h-[280px] rounded-full bg-emerald-500/8 blur-3xl" />
      </div>
      <div className="relative">
        <div className="flex items-start gap-4 mb-5">
          <span className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shrink-0">
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
              <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
            </svg>
          </span>
          <div className="min-w-0">
            <div className="text-base font-semibold text-ink">Connect your AI agent</div>
            <div className="text-sm text-dim">
              Sendepot speaks MCP. Drop the URL + your API token into Claude, Cursor, or any MCP client.
            </div>
          </div>
        </div>

        {/* MCP URL */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 px-3 py-2.5 bg-bg/40 border border-line rounded-md font-mono text-[12.5px]">
            <span className="text-dim">URL</span>
            <code className="flex-1 text-ink truncate">https://sendepot.com/mcp</code>
            <CopyButton text="https://sendepot.com/mcp" />
          </div>
          <div className="flex items-center gap-2 px-3 py-2.5 bg-bg/40 border border-line rounded-md font-mono text-[12.5px]">
            <span className="text-dim">Header</span>
            <code className="flex-1 text-ink truncate">Authorization: Bearer {previewKey}</code>
            <CopyButton text={`Authorization: Bearer ${pasted.startsWith('ie_') ? pasted : 'ie_YOUR_TOKEN_HERE'}`} />
          </div>

          {/* Paste-your-key */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="input flex-1 font-mono text-[12.5px]"
              placeholder="Paste your full API token here (ie_…)"
              value={pasted}
              onChange={e => setPasted(e.target.value)}
            />
            <Link to="/tokens" className="btn-secondary btn-sm whitespace-nowrap">
              {hasToken ? 'Manage tokens' : 'Get API key →'}
            </Link>
          </div>
          <p className="text-[11px] text-dim">
            {hasToken
              ? `You have ${tokenLabel ? `"${tokenLabel}" + ` : ''}an active token. Pasting it here updates the snippets below in place — nothing is sent to a server.`
              : 'You don\'t have a token yet. Click "Get API key" to mint one (read or read+write scopes).'}
          </p>
        </div>
      </div>
    </section>
  );
}


// ── Setup guides ─────────────────────────────────────────────────────────

type Tab = 'claude-code' | 'claude-desktop' | 'cursor' | 'http' | 'curl';

function SetupGuides() {
  const [tab, setTab] = useState<Tab>('claude-code');

  return (
    <section className="mb-12">
      <SectionHead
        icon={<IconCode />}
        title="Detailed setup guides"
        sub="Step-by-step instructions for each platform."
      />
      <div className="card p-0 overflow-hidden">
        {/* Tab strip */}
        <div className="flex gap-px border-b border-line/60 overflow-x-auto">
          <TabButton active={tab === 'claude-code'}    onClick={() => setTab('claude-code')}    label="Claude Code"     pill="recommended" />
          <TabButton active={tab === 'claude-desktop'} onClick={() => setTab('claude-desktop')} label="Claude Desktop"  pill="MCP UI" />
          <TabButton active={tab === 'cursor'}         onClick={() => setTab('cursor')}         label="Cursor"          />
          <TabButton active={tab === 'http'}           onClick={() => setTab('http')}           label="HTTP / SDK"      />
          <TabButton active={tab === 'curl'}           onClick={() => setTab('curl')}           label="cURL"            />
        </div>
        <div className="p-6 space-y-5">
          {tab === 'claude-code'    && <ClaudeCodeGuide />}
          {tab === 'claude-desktop' && <ClaudeDesktopGuide />}
          {tab === 'cursor'         && <CursorGuide />}
          {tab === 'http'           && <HTTPGuide />}
          {tab === 'curl'           && <CurlGuide />}
          <Link to="/docs/agents/overview" className="text-xs text-accent hover:underline inline-flex items-center gap-1.5">
            <IconBook /> Read full integration docs ↗
          </Link>
        </div>
      </div>
    </section>
  );
}

function TabButton({
  active, onClick, label, pill,
}: { active: boolean; onClick: () => void; label: string; pill?: string }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 text-sm transition border-b-2 whitespace-nowrap ${
        active
          ? 'text-ink border-accent bg-accent/5'
          : 'text-dim border-transparent hover:text-ink hover:bg-white/[0.02]'
      }`}
    >
      <span>{label}</span>
      {pill && (
        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
          pill === 'recommended' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-accent/15 text-accent'
        }`}>{pill}</span>
      )}
    </button>
  );
}

function StepHead({ n, label }: { n: number; label: string }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <span className="w-5 h-5 rounded-full bg-accent/15 border border-accent/40 text-accent text-[10px] font-mono flex items-center justify-center">
        {n}
      </span>
      <span className="text-sm font-medium text-ink">{label}</span>
    </div>
  );
}

function CodeBlock({ lang, code }: { lang: string; code: string }) {
  return (
    <div className="rounded-md border border-line bg-bg/40 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-line/60 bg-white/[0.02]">
        <span className="text-[10px] uppercase tracking-wider text-dim">{lang}</span>
        <CopyButton text={code} />
      </div>
      <pre className="px-4 py-3 font-mono text-[12.5px] text-ink overflow-x-auto whitespace-pre">{code}</pre>
    </div>
  );
}

function ClaudeCodeGuide() {
  return (
    <>
      <p className="text-sm text-dim leading-relaxed">
        The fastest path. One command and Claude Code can drive Sendepot in plain English.
      </p>
      <div className="rounded-md border-l-2 border-emerald-500/40 bg-emerald-500/5 px-4 py-2.5">
        <div className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1">After setup</div>
        <div className="text-xs text-ink/90">Just ask Claude: <span className="font-mono text-ink">"List my failing Sendepot tenants and tell me which sequencer key each uses."</span></div>
      </div>
      <div>
        <StepHead n={1} label="Run this in your terminal" />
        <CodeBlock lang="bash" code={`claude mcp add --transport http sendepot \\
  https://sendepot.com/mcp \\
  --header "Authorization: Bearer ie_YOUR_TOKEN_HERE"`} />
        <p className="text-[11px] text-dim mt-2">Replace <code className="font-mono text-ink/80">ie_YOUR_TOKEN_HERE</code> with the token from /tokens.</p>
      </div>
      <div>
        <StepHead n={2} label="Verify the connection" />
        <CodeBlock lang="bash" code="claude mcp list" />
        <p className="text-[11px] text-dim mt-2">You should see <code className="font-mono text-ink/80">sendepot</code> in the active list.</p>
      </div>
    </>
  );
}

function ClaudeDesktopGuide() {
  return (
    <>
      <p className="text-sm text-dim leading-relaxed">
        Claude Desktop supports remote MCP servers natively — no JSON config to edit.
      </p>
      <div>
        <StepHead n={1} label="Settings → Connectors → Add custom connector" />
        <ul className="list-disc pl-5 space-y-1 text-sm text-ink/90 mb-2">
          <li>Server URL: <code className="font-mono text-ink">https://sendepot.com/mcp</code></li>
          <li>Auth: paste your <code className="font-mono text-ink">ie_…</code> token as Bearer</li>
          <li>Save</li>
        </ul>
      </div>
      <div>
        <StepHead n={2} label="Use it" />
        <p className="text-sm text-ink/90">The Sendepot tools appear in the 🔧 menu next to the message composer.</p>
      </div>
    </>
  );
}

function CursorGuide() {
  return (
    <>
      <p className="text-sm text-dim leading-relaxed">
        Cursor → Settings → MCP → <strong>+ Add new MCP server</strong>. Paste this JSON:
      </p>
      <CodeBlock lang="json" code={`{
  "mcpServers": {
    "sendepot": {
      "url": "https://sendepot.com/mcp",
      "headers": {
        "Authorization": "Bearer ie_YOUR_TOKEN_HERE"
      }
    }
  }
}`} />
    </>
  );
}

function HTTPGuide() {
  return (
    <>
      <p className="text-sm text-dim leading-relaxed">
        Same auth as MCP — Bearer <code className="font-mono text-ink">ie_…</code> on the
        REST endpoints under <code className="font-mono text-ink">/api</code>.
      </p>
      <CodeBlock lang="js" code={`const res = await fetch('https://sendepot.com/api/tenants', {
  headers: { 'Authorization': 'Bearer ie_YOUR_TOKEN_HERE' },
});
const tenants = await res.json();`} />
      <p className="text-[11px] text-dim">Full reference at <a href="/api/docs" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">/api/docs</a>.</p>
    </>
  );
}

function CurlGuide() {
  return (
    <>
      <p className="text-sm text-dim leading-relaxed">Smoke-test your token in two seconds.</p>
      <CodeBlock lang="bash" code={`curl https://sendepot.com/api/tenants \\
  -H "Authorization: Bearer ie_YOUR_TOKEN_HERE"`} />
    </>
  );
}


// ── Section helpers + small components ───────────────────────────────────

function SectionHead({ icon, title, sub }: { icon: React.ReactNode; title: string; sub: string }) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <span className="w-8 h-8 rounded-lg bg-accent/12 border border-accent/30 text-accent flex items-center justify-center shrink-0">
        {icon}
      </span>
      <div>
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        <div className="text-xs text-dim">{sub}</div>
      </div>
    </div>
  );
}

function Capability({
  tone, icon, title, body,
}: {
  tone: 'accent' | 'run' | 'emerald' | 'warn' | 'purple' | 'cyan';
  icon: React.ReactNode; title: string; body: string;
}) {
  const styles = {
    accent:  'border-accent/30  bg-accent/5  text-accent',
    run:     'border-run/30     bg-run/5     text-run',
    emerald: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-400',
    warn:    'border-warn/30    bg-warn/5    text-warn',
    purple:  'border-purple-400/30  bg-purple-400/5  text-purple-300',
    cyan:    'border-cyan-400/30 bg-cyan-400/5 text-cyan-300',
  }[tone];
  const [borderCls, bgCls, textCls] = styles.split(' ');
  return (
    <div className={`card p-5 hover:bg-white/[0.04] transition relative overflow-hidden`}>
      <div className={`w-10 h-10 rounded-lg border ${borderCls} ${bgCls} ${textCls} flex items-center justify-center mb-3`}>
        {icon}
      </div>
      <div className="text-sm font-semibold text-ink mb-1.5">{title}</div>
      <div className="text-xs text-dim leading-relaxed">{body}</div>
    </div>
  );
}

function ResourceCard({
  to, href, external, icon, title, sub,
}: {
  to?: string; href?: string; external?: boolean;
  icon: React.ReactNode; title: string; sub: string;
}) {
  const inner = (
    <div className="card p-4 hover:bg-white/[0.04] transition flex items-center gap-3 group">
      <span className="w-9 h-9 rounded-lg bg-white/[0.03] border border-line text-dim flex items-center justify-center shrink-0 group-hover:text-ink group-hover:border-line2 transition">
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-ink flex items-center gap-1.5">
          {title} {external && <span className="text-[10px] opacity-60">↗</span>}
        </div>
        <div className="text-[11px] text-dim truncate">{sub}</div>
      </div>
      <span className="text-dim group-hover:text-accent transition">→</span>
    </div>
  );
  if (href) return <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined}>{inner}</a>;
  return <Link to={to!}>{inner}</Link>;
}


// ── Copy button ──────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard rejected — silent */ }
      }}
      className="text-[11px] text-dim hover:text-ink transition px-2 py-1 rounded shrink-0"
    >
      {copied ? '✓ copied' : 'copy'}
    </button>
  );
}


// ── Tiny inline SVGs ─────────────────────────────────────────────────────

function svg({ children, className = 'w-4 h-4' }: { children: React.ReactNode; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}

function IconBolt()    { return svg({ children: <path d="M13 3 L4 14 H11 L9 21 L20 10 H13 Z" /> }); }
function IconShield()  { return svg({ children: <path d="M12 3 L20 6 V12 C20 17 16 20.5 12 21 C8 20.5 4 17 4 12 V6 Z" /> }); }
function IconRefresh() { return svg({ children: <><path d="M3 12a9 9 0 0 1 14.5-7l1.5 1.5" /><path d="M21 4v5h-5" /><path d="M21 12a9 9 0 0 1-14.5 7L5 17.5" /><path d="M3 20v-5h5" /></> }); }
function IconSwap()    { return svg({ children: <><path d="M7 4 L4 7 L7 10" /><path d="M4 7 H17 A3 3 0 0 1 20 10 V11" /><path d="M17 20 L20 17 L17 14" /><path d="M20 17 H7 A3 3 0 0 1 4 14 V13" /></> }); }
function IconKey()     { return svg({ children: <><circle cx="8" cy="15" r="3" /><path d="M10 13 L20 3 L18 1 M17 7 L19 9" /></> }); }
function IconExport()  { return svg({ children: <><path d="M12 3 V15" /><path d="M7 8 L12 3 L17 8" /><path d="M5 15 V19 A2 2 0 0 0 7 21 H17 A2 2 0 0 0 19 19 V15" /></> }); }
function IconBook()    { return svg({ children: <path d="M4 4 H12 A4 4 0 0 1 16 8 V20 H6 A2 2 0 0 1 4 18 Z M16 20 V8 A4 4 0 0 1 20 4" /> }); }
function IconGrid()    { return svg({ children: <><rect x="3" y="3"  width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></> }); }
function IconCode()    { return svg({ children: <><path d="M8 6 L2 12 L8 18" /><path d="M16 6 L22 12 L16 18" /><path d="M14 4 L10 20" /></> }); }
function IconSparkle() { return svg({ children: <><path d="M12 3 L13.5 9 L20 12 L13.5 15 L12 21 L10.5 15 L4 12 L10.5 9 Z" /></> }); }
